import "core-js/modules/es6.object.define-properties";
import "core-js/modules/es6.object.freeze";
import "core-js/modules/es6.symbol";
import "core-js/modules/es6.array.index-of";
import "core-js/modules/web.dom.iterable";
import "core-js/modules/es6.array.iterator";
import "core-js/modules/es6.object.to-string";
import "core-js/modules/es6.object.keys";
import "core-js/modules/es6.object.assign";

/**
 * Copyright Zendesk, Inc.
 *
 * Use of this source code is governed under the Apache License, Version 2.0
 * found at http://www.apache.org/licenses/LICENSE-2.0.
 */
import React, { Children } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { readableColor, math } from 'polished';
import { retrieveComponentStyles, DEFAULT_THEME, getLineHeight, getColor } from '@zendeskgarden/react-theming';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _templateObject() {
  var data = _taggedTemplateLiteral(["\n  flex-shrink: 0;\n  font-size: 0; /* text content reset */\n\n  ", ";\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}

var COMPONENT_ID = 'tags.avatar';
var StyledAvatar = styled(function (_ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, ["children"]);

  return React.cloneElement(Children.only(children), props);
}).attrs({
  'data-garden-id': COMPONENT_ID,
  'data-garden-version': '8.2.0'
})(_templateObject(), function (props) {
  return retrieveComponentStyles(COMPONENT_ID, props);
});
StyledAvatar.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$1() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  flex-shrink: 0;\n  align-items: center;\n  justify-content: center;\n  transition: opacity 0.25s ease-in-out;\n  opacity: 0.8;\n  border: 0; /* <button> element reset */\n  cursor: pointer;\n  padding: 0; /* <button> element reset */\n  font-size: 0; /* text content reset */\n\n  &:hover {\n    opacity: 0.9;\n  }\n\n  &:focus {\n    outline: none;\n  }\n\n  ", ";\n"]);

  _templateObject$1 = function _templateObject() {
    return data;
  };

  return data;
}

var COMPONENT_ID$1 = 'tags.close';
var StyledClose = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$1,
  'data-garden-version': '8.2.0',
  'aria-label': 'Press delete to remove'
})(_templateObject$1(), function (props) {
  return retrieveComponentStyles(COMPONENT_ID$1, props);
});
StyledClose.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$2() {
  var data = _taggedTemplateLiteral(["\n  display: inline-flex;\n  flex-wrap: nowrap;\n  align-items: center;\n  justify-content: ", ";\n  transition: box-shadow 0.1s ease-in-out;\n  box-sizing: border-box;\n  border: 0; /* <button> element reset */\n  max-width: 100%;\n  overflow: hidden;\n  vertical-align: middle;\n  text-decoration: none; /* <a> element reset */\n  white-space: nowrap;\n  font-weight: ", ";\n  direction: ", ";\n\n  ", ";\n\n  &:hover {\n    cursor: default;\n    text-decoration: none;\n  }\n\n  &:focus {\n    outline: none;\n  }\n\n  &:link:hover,\n  &:visited:hover {\n    cursor: pointer;\n  }\n\n  &:any-link:hover {\n    cursor: pointer;\n  }\n\n  &[data-garden-focus-visible] {\n    text-decoration: none;\n  }\n\n  ", ";\n\n  & > * {\n    overflow: hidden;\n    text-align: center;\n    text-overflow: ellipsis;\n    white-space: nowrap;\n  }\n\n  & ", " {\n    display: ", ";\n  }\n\n  & ", " {\n    display: ", ";\n  }\n\n  ", ";\n"]);

  _templateObject$2 = function _templateObject() {
    return data;
  };

  return data;
}

var COMPONENT_ID$2 = 'tags.tag_view';

var colorStyles = function colorStyles(props) {
  var backgroundColor;
  var boxShadowColor;
  var foregroundColor;
  var closeColor;

  if (props.hue) {
    var shade = props.hue === 'yellow' ? 400 : 600;
    backgroundColor = getColor(props.hue, shade, props.theme);
    boxShadowColor = getColor(props.hue, shade, props.theme, 0.35);

    if (props.hue === 'yellow' || props.hue === 'lemon') {
      foregroundColor = getColor('yellow', 800, props.theme);
    } else {
      foregroundColor = readableColor(backgroundColor, props.theme.colors.foreground, props.theme.colors.background);
    }
  } else {
    backgroundColor = getColor('neutralHue', 200, props.theme);
    boxShadowColor = getColor('neutralHue', 600, props.theme, 0.35);
    foregroundColor = getColor('neutralHue', 700, props.theme);
    closeColor = getColor('neutralHue', 600, props.theme);
  }

  return css(["background-color:", ";color:", ";&:hover{color:", ";}&:focus{box-shadow:", ";}& ", "{color:", ";}"], backgroundColor, foregroundColor, foregroundColor, props.theme.shadows.sm(boxShadowColor), StyledClose, closeColor);
};

var sizeStyles = function sizeStyles(props) {
  var borderRadius;
  var padding;
  var height;
  var fontSize;
  var minWidth;
  var avatarSize;

  if (props.size === 'small') {
    borderRadius = props.theme.borderRadii.sm;
    padding = props.theme.space.base;
    height = props.theme.space.base * 4;
    fontSize = props.theme.fontSizes.xs;
    avatarSize = 0;
  } else if (props.size === 'large') {
    borderRadius = props.theme.borderRadii.md;
    padding = props.theme.space.base * 3;
    height = props.theme.space.base * 8;
    fontSize = props.theme.fontSizes.sm;
    avatarSize = props.theme.space.base * 6;
  } else {
    borderRadius = props.theme.borderRadii.sm;
    padding = props.theme.space.base * 2;
    height = props.theme.space.base * 5;
    fontSize = props.theme.fontSizes.sm;
    avatarSize = props.theme.space.base * 4;
  }

  var avatarBorderRadius = props.size === 'large' ? math("".concat(borderRadius, " - 1")) : borderRadius;
  var avatarMargin = (height - avatarSize) / 2;
  var avatarTextMargin = props.isRound ? avatarMargin : avatarMargin * 2;

  if (props.isRound) {
    borderRadius = '50%';
    padding = 0;
    minWidth = height;
    avatarBorderRadius = '50%';
  } else if (props.isPill) {
    borderRadius = '100px';
    avatarBorderRadius = '50%';

    if (props.size === 'small') {
      padding = props.theme.space.base * 1.5;
      minWidth = props.theme.space.base * 6;
    } else if (props.size === 'large') {
      minWidth = props.theme.space.base * 12;
    } else {
      minWidth = props.theme.space.base * 7.5;
    }
  }

  return css(["border-radius:", ";padding:0 ", "px;min-width:", ";height:", "px;line-height:", ";font-size:", ";& > *{width:100%;min-width:", ";}& ", "{margin-", ":-", "px;margin-", ":", "px;border-radius:", ";width:", "px;min-width:", "px;height:", "px;}& ", "{margin-", ":-", "px;border-radius:", ";width:", "px;height:", "px;}"], borderRadius, padding, minWidth ? "".concat(minWidth, "px") : "calc(".concat(padding * 2, "px + 1ch)"), height, getLineHeight(height, fontSize), fontSize, minWidth ? "".concat(minWidth - padding * 2, "px") : '1ch', StyledAvatar, props.theme.rtl ? 'right' : 'left', padding - avatarMargin, props.theme.rtl ? 'left' : 'right', avatarTextMargin, avatarBorderRadius, avatarSize, avatarSize, avatarSize, StyledClose, props.theme.rtl ? 'left' : 'right', padding, borderRadius, height, height);
};

var StyledTag = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$2,
  'data-garden-version': '8.2.0'
})(_templateObject$2(), function (props) {
  return props.isRound && 'center';
}, function (props) {
  return props.theme.fontWeights.semibold;
}, function (props) {
  return props.theme.rtl ? 'rtl' : 'ltr';
}, function (props) {
  return sizeStyles(props);
}, function (props) {
  return colorStyles(props);
}, StyledAvatar, function (props) {
  return (props.isRound || props.size === 'small') && 'none';
}, StyledClose, function (props) {
  return props.isRound && 'none';
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$2, props);
});
StyledTag.defaultProps = {
  size: 'medium',
  theme: DEFAULT_THEME
};

function _extends$1() {
  _extends$1 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$1.apply(this, arguments);
}

var _ref = /*#__PURE__*/React.createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M3 9l6-6m0 6L3 3"
});

function SvgXStroke(props) {
  return React.createElement("svg", _extends$1({
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref);
}

var Close = React.forwardRef(function (props, ref) {
  return React.createElement(StyledClose, _extends({
    ref: ref
  }, props), React.createElement(SvgXStroke, null));
});
Close.displayName = 'Close';
var Tag = React.forwardRef(function (_ref, ref) {
  var size = _ref.size,
      hue = _ref.hue,
      otherProps = _objectWithoutProperties(_ref, ["size", "hue"]);

  return React.createElement(StyledTag, _extends({
    ref: ref,
    size: size,
    hue: hue
  }, otherProps));
});
Tag.displayName = 'Tag';
Tag.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  hue: PropTypes.oneOf(['grey', 'blue', 'kale', 'red', 'green', 'yellow', 'fuschia', 'pink', 'crimson', 'orange', 'lemon', 'lime', 'mint', 'teal', 'azure', 'royal', 'purple']),
  isPill: PropTypes.bool,
  isRound: PropTypes.bool
};
Tag.defaultProps = {
  size: 'medium'
};
Tag.Avatar = StyledAvatar;
Tag.Close = Close;
export { Tag };